import {
  ProfilePageTabs,
  getMyProfileUrl,
  getProfileUrl,
} from '@/presentation/services/urls/profile';
import { useRouter } from 'next/router';
import { ProfileComment } from '../../../../../../domain/entities/Comment';
import { ProfileOffer } from '../../../../../../domain/entities/Offer';
import { ProfileUser } from '../../../../../../domain/entities/User';
import { removeVote, vote } from '../../../../../../infra/api/votes/endpoints';
import { useCurrentUserCache } from '../../../../../hooks/useCurrentUserCache';
import CommentsList from '../CommentsList';
import DealsList from '../DealsList';
import ProfileTabs from '../DealsTabs';

type DefaultContentProps = {
  profileUser: Omit<ProfileUser, 'comments' | 'offers'>;
  comments: ProfileComment[];
  commentsHasNextPage?: boolean;
  commentsLoading?: boolean;
  deals: ProfileOffer[];
  dealsHasNextPage?: boolean;
  dealsLoading?: boolean;
  tab: ProfilePageTabs;
  onLoadMoreComments: () => void;
  onLoadMoreDeals: () => void;
};

function EnhancedDefaultContent({
  onLoadMoreComments,
  onLoadMoreDeals,
  profileUser,
  deals,
  dealsHasNextPage,
  dealsLoading,
  comments,
  commentsHasNextPage,
  commentsLoading,
  tab,
}: DefaultContentProps) {
  const { locale } = useRouter();
  const { loggedUser } = useCurrentUserCache();
  const loggedUserId = loggedUser?.id;

  const isOwner = !!loggedUserId && loggedUserId === profileUser.id;

  const dealsTabUrl = isOwner
    ? getMyProfileUrl({ tab: ProfilePageTabs.Deals }, locale)
    : getProfileUrl(
        profileUser.nickname,
        { tab: ProfilePageTabs.Deals },
        locale
      );

  const commentsTabUrl = isOwner
    ? getMyProfileUrl({ tab: ProfilePageTabs.Comments }, locale)
    : getProfileUrl(
        profileUser.nickname,
        { tab: ProfilePageTabs.Comments },
        locale
      );

  const dealsTabContent = (
    <DealsList
      isOwner={isOwner}
      isLoading={dealsLoading}
      deals={deals}
      userNickname={profileUser.nickname}
      hasNextPage={dealsHasNextPage}
      onLoadMore={onLoadMoreDeals}
      onVote={vote.requestAsPromise}
      onRemoveVote={removeVote.requestAsPromise}
    />
  );

  const commentsTabContent = (
    <CommentsList
      isOwner={isOwner}
      isLoading={commentsLoading}
      onLoadMore={onLoadMoreComments}
      comments={comments}
      userNickname={profileUser.nickname}
      isVerified={profileUser.isVerified}
      hasNextPage={commentsHasNextPage}
      userImage={profileUser.image?.url}
      onVote={vote.requestAsPromise}
      onRemoveVote={removeVote.requestAsPromise}
    />
  );

  return (
    <ProfileTabs
      totalComments={profileUser.totalComments}
      totalOffers={profileUser.totalOffers}
      initialTab={tab}
      dealsTabContent={dealsTabContent}
      commentsTabContent={commentsTabContent}
      dealsTabUrl={dealsTabUrl}
      commentsTabUrl={commentsTabUrl}
    />
  );
}

export default EnhancedDefaultContent;
